
import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { BoardCategory } from '@/types/board';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPost } from '@/services/postService';
import { useNavigate } from 'react-router-dom';
import { LinkShareForm } from './LinkShareForm';
import QuillEditor from './QuillEditor';
import CategorySelector from './CategorySelector';
import FormActions from './FormActions';

interface BoardWriteFormProps {
  initialCategory?: BoardCategory;
  initialTitle?: string;
  initialContent?: string;
  user: any;
  onSubmit?: (title: string, content: string, category: BoardCategory) => void;
  isEdit?: boolean;
}

export const BoardWriteForm = ({ 
  initialCategory, 
  initialTitle = '',
  initialContent = '',
  user,
  onSubmit,
  isEdit = false
}: BoardWriteFormProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(initialContent);
  const [category, setCategory] = useState<BoardCategory>(initialCategory || '자유게시판');
  
  useEffect(() => {
    // Update form values when initialValues change (for editing mode)
    if (initialTitle) setTitle(initialTitle);
    if (initialContent) setContent(initialContent);
    if (initialCategory) setCategory(initialCategory);
  }, [initialTitle, initialContent, initialCategory]);
  
  const categories: BoardCategory[] = [
    '자유게시판',
    '법률정보',
    '구인구직',
    '사고팔고',
    '부동산',
    'Weee Link 공유'
  ];
  
  // Create post mutation - only used when creating new posts
  const createPostMutation = useMutation({
    mutationFn: () => createPost(title, content, category, user),
    onSuccess: (data) => {
      toast({
        title: "게시글이 작성되었습니다",
        description: "게시판으로 이동합니다."
      });
      
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      navigate(`/board/post/${data.id}`);
    },
    onError: (error) => {
      console.error('Error submitting post:', error);
      toast({
        variant: "destructive",
        title: "게시글 작성 실패",
        description: "오류가 발생했습니다. 다시 시도해주세요."
      });
    }
  });
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "제목을 입력해주세요",
        description: "게시글 제목은 필수 항목입니다."
      });
      return;
    }
    
    if (!content.trim()) {
      toast({
        variant: "destructive",
        title: "내용을 입력해주세요",
        description: "게시글 내용은 필수 항목입니다."
      });
      return;
    }

    if (isEdit && onSubmit) {
      onSubmit(title, content, category);
    } else {
      createPostMutation.mutate();
    }
  };

  // If the category is 'Weee Link 공유', render the link share form
  if (category === 'Weee Link 공유' && !isEdit) {
    return (
      <>
        <CategorySelector 
          value={category}
          onChange={setCategory}
          categories={categories}
        />
        
        <LinkShareForm user={user} />
      </>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl mx-auto">
      <CategorySelector 
        value={category}
        onChange={setCategory}
        categories={categories}
      />
      
      <div className="space-y-2">
        <label htmlFor="title" className="block text-sm font-medium">
          제목
        </label>
        <Input
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="제목을 입력하세요"
          maxLength={100}
          required
        />
      </div>
      
      <div className="space-y-2">
        <label htmlFor="content" className="block text-sm font-medium">
          내용
        </label>
        <QuillEditor 
          value={content}
          onChange={setContent}
        />
      </div>
      
      <FormActions 
        isPending={createPostMutation.isPending}
        submitLabel={isEdit ? "수정하기" : "게시하기"}
        loadingLabel={isEdit ? "수정 중..." : "게시 중..."}
      />
    </form>
  );
};
